import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { Airport } from '../models/airport.model';
import { AirportFeeSummaryComponent } from '../airport-fee-summary/airport-fee-summary.component';
import { QuickNavComponent } from '../../shared/quick-nav/quick-nav.component';
import { BusinessManagerService } from '../../shared/user/business-manager.service';
import { EditToggleService } from '../../shared/services/edit-toggle.service';
import { MatDialog, MatSnackBar } from '@angular/material';
import { Router } from '@angular/router';
import { AirportService } from '../airport.service';
import { BusinessSummary } from '../models/business-summary.model';
import { AddBusinessDialogComponent } from '../add-business-dialog/add-business-dialog.component';
import { EditNavaidComponent } from '../edit-navaid/edit-navaid.component';
import { EditCommunicationComponent } from '../edit-communication/edit-communication.component';
import { Source } from '../../shared/models/source.model';
import { QuickNavToc } from '../../shared/models/quick-nav-toc.model';
import { EditOperationsComponent } from '../edit-operations/edit-operations.component';

@Component({
  selector: 'app-airport-island-detail',
  templateUrl: './airport-island-detail.component.html',
  styleUrls: ['./airport-island-detail.component.scss']
})
export class AirportIslandDetailComponent implements AfterViewInit, OnInit {
  @Input() airport: Airport;

  @ViewChild(AirportFeeSummaryComponent) feesSection: AirportFeeSummaryComponent;
  @ViewChild('quicknav') quicknav: QuickNavComponent;
  @ViewChild('body', { read: ElementRef }) body: ElementRef;

  toc: QuickNavToc = new QuickNavToc('airport-quicknav', [{
    label: 'Overview',
    fragment: 'overview',
    visible: true
  }, {
    label: 'FBOs',
    fragment: 'fbos',
    visible: true
  }, {
    label: 'Food/Lodging/Activities',
    fragment: 'local',
    visible: true
  }, {
    label: 'Flying Clubs',
    fragment: 'flyingclubs',
    visible: true
  }, /* commented out temporarily per Eric on 3/9/2018
  {
    label: 'Fees',
    fragment: 'fees',
    visible: () => this.feesSection.hasContent
  },*/ {
    label: 'Runways',
    fragment: 'runways',
    visible: true
  }, {
    label: 'Procedures',
    fragment: 'procedures',
    visible: true
  }, {
    label: 'Operations',
    fragment: 'operations',
    visible: true
  }, {
    label: 'Comms',
    fragment: 'comms',
    visible: true
  }, {
    label: 'Weather',
    fragment: 'wx',
    visible: true
  }]);

  isFlightDeckAdmin = false;
  editModeSet = false;
  isAirportManager = false;
  oldAirport: Airport;
  isSubscribedToIslandGuide = false;
  isCaribbeanOrBahamasIsland = false;

  constructor( private _businessManagerService: BusinessManagerService,
               private _toggleService: EditToggleService,
               private _matDialog: MatDialog,
               private _router: Router,
               private _airportService: AirportService,
               private _snackBar: MatSnackBar
  ) {
  }

  /**
   * Returns a subset of the list of businesses at this airport that either (1) have overview fees reported, or (2)
   * provide fuel. This is used for displaying the fee summary section, which is meant to list all FBOs which may
   * have fees. In the case where fees are not reported, a placeholder message is shown saying the location has not
   * disclosed fees.
   */
  get businessesWithFees(): Array<BusinessSummary> {
    return this.airport.businesses.filter(b => AirportService.shouldShowFees(b));
  }

  async ngOnInit() {
    this.isAirportManager = await this._businessManagerService.userManagesAirport(this.airport.airportId);
    this.isFlightDeckAdmin = await this._businessManagerService.isFlightDeckAdmin();
    this.isSubscribedToIslandGuide = this.getIslandGuideSubscriptionState(
      await this._businessManagerService.isSubscribedToCaribbeanGuide(), await this._businessManagerService.isSubscribedToBahamasGuide());
    this.isCaribbeanOrBahamasIsland = this.airport.sourceId === Source.CaribbeanGuide || this.airport.sourceId === Source.BahamasGuide;
    this._toggleService.toggleEmitter.subscribe(isOn => {
      this.editModeSet = isOn;
    });
    this.oldAirport = this.airport;
  }

  /* async ngDoCheck() {
     /
      * This hack is to handle routes coming from the autocomplete search.
      * Without it, an airport manager would look like they could manage airports they don't have permission to manage.
      * The API prevents any actual changes regardless of what the interface shows without this hack.
      /
    if (this.airport && this.oldAirport && this.airport.airportId !== this.oldAirport.airportId) {
      this.oldAirport = this.airport;
      this.isAirportManager = await this._businessManagerService.userManagesAirport(this.airport.airportId);
      if (!this.isAirportManager) {
        this._toggleService.setState(false);
      }
    }
  } */

  async ngAfterViewInit() {
    /*
    const $detailBody = $(this.body.nativeElement);
    const $page = $detailBody.closest('.detail-page');
    this.quicknav.setContainerHeight($page.height() + 1000);
    */
  }

  get sortedFBOs(): BusinessSummary[] {
    if (!this.isFlightDeckAdmin) {
      return this.airport.businesses
        .filter(b => b.isFbo)
        .filter(fbo => !fbo.hideBusiness)
        .filter(fbo => !fbo.notApproved)
        .sort((a, b) => {
          if (a.name < b.name) {
            return -1;
          }
          if (a.name > b.name) {
            return 1;
          }
          return 0;
        });
    }
    return this.airport.businesses
      .filter(b => b.isFbo)
      .sort((a, b) => {
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }
        return 0;
      });
  }

  async onReloadAirport(reload: boolean) {
    this.airport = await this._airportService.getAirport(this.airport.identifier, true);
  }

  async reloadAirport(identifier: string) {
    this.airport = await this._airportService.getAirport(identifier, true);
  }

  openAddDialog(facilityType: string): void {
    const dialogRef = this._matDialog.open(AddBusinessDialogComponent, {
      disableClose: false,
      autoFocus: true,
      data: {
        airport: this.airport,
        facilityType: facilityType
      }
    });
    dialogRef.afterClosed().subscribe(closeDialog => {
      if (closeDialog.cancelButtonClicked) {
        return;
      }
      if (closeDialog.success) {
        let type: string;
        if (facilityType === 'FLYING CLUB') {
          type = 'flyingclub';
        } else if (facilityType === 'BUSINESS') {
          type = 'business';
        }
        this.reloadAirport(this.airport.identifier);
        this._router.navigateByUrl(`/${type}/${closeDialog.payload}`);
      }
    });
  }

  openAddNavaidDialog(): void {
    let ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    const dialogRef = this._matDialog.open(EditNavaidComponent, {
      disableClose: false,
      autoFocus: true,
      data: {
        airportId: this.airport.airportId,
        isNew: true,
        navaid: null
      }
    });
    dialogRef.afterClosed().subscribe(closeDialog => {
      if (!closeDialog) { return; } // required to handle backdrop click.
      if (closeDialog.cancelBtnClicked) {
        return;
      }
      if (closeDialog.success) {
        this.reloadAirport(this.airport.identifier);
        this._snackBar.open('Navaid', 'Added', {
          duration: 2000
        });
      } else {
        if (closeDialog.error != null) {
          ERROR_MSG = closeDialog.error.message;
        }
        this._snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }

  openAddCommsDialog(): void {
    let ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    const dialogRef = this._matDialog.open(EditCommunicationComponent, {
      disableClose: false,
      autoFocus: true,
      data: {
        airportId: this.airport.airportId,
        isNew: true,
        comm: null
      }
    });
    dialogRef.afterClosed().subscribe(closeDialog => {
      if (!closeDialog) { return; } // required to handle backdrop click.
      if (closeDialog.cancelBtnClicked) {
        return;
      }
      if (closeDialog.success) {
        this.reloadAirport(this.airport.identifier);
        this._snackBar.open('Communication', 'Added', {
          duration: 2000
        });
      } else {
        if (closeDialog.error != null) {
          ERROR_MSG = closeDialog.error.message;
        }
        this._snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }

  openEditOperationsDialog(airport: Airport): void {
    let ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    const dialogRef = this._matDialog.open(EditOperationsComponent, {
      disableClose: false,
      autoFocus: true,
      data: {
        airport: this.airport
      }
    });
    dialogRef.afterClosed().subscribe(closeDialog => {
      if (!closeDialog) { return; } // required to handle backdrop click.
      if (closeDialog.cancelBtnClicked) {
        return;
      }
      if (closeDialog.success) {
        this.reloadAirport(this.airport.identifier);
        this._snackBar.open('Operations', 'Updated', {
          duration: 2000
        });
      } else {
        if (closeDialog.error != null) {
          ERROR_MSG = closeDialog.error.message;
        }
        this._snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }

  getIslandGuideSubscriptionState(hasCaribbeanAccess: boolean, hasBahamasAccess: boolean): boolean {
    if ((this.airport.sourceId === Source.CaribbeanGuide && hasCaribbeanAccess) ||
      (this.airport.sourceId === Source.BahamasGuide && hasBahamasAccess)
      || this.isAirportManager) {
      return  true;
    } else {
      return false;
    }
  }
}
